import * as THREE from 'three';

/**
 * surfaceMaterials.ts — real PBR ground/road materials from a Poly Haven (CC0)
 * texture set (albedo + normal + ARM). The premium-realism upgrade over the
 * shared procedural asphalt in surfaceTextures.ts: a city's road can opt into a
 * genuine cracked-tarmac surface (or a concrete verge) that catches the dusk sun
 * and IBL like a real street. Cities without a set keep the procedural look.
 *
 * "ARM" packs Ambient-occlusion / Roughness / Metalness into R/G/B — three.js
 * reads roughness from the GREEN channel and metalness from BLUE when the same
 * texture is assigned to roughnessMap + metalnessMap, so one fetch drives both.
 * (AO needs a 2nd UV set the track ribbons don't have, so it's left off — its
 * contribution on a tiling road is negligible anyway.)
 *
 * Textures are cached + shared across city loads (the disposer skips `shared`
 * maps); the lightweight material is rebuilt per load, matching the asphalt
 * pattern in surfaceTextures.ts / Game.disposeObject.
 */
export interface SurfaceTextureSpec {
  /** Folder under public/ holding diffuse.jpg, normal.jpg, arm.jpg. */
  base: string;
  /** UV repeat across [width, length] of the ribbon. */
  repeat: [number, number];
  /** Multiply tint over the albedo (default white — show the true texture). */
  tint?: number;
  /** Normal-map strength (default 1). */
  normalScale?: number;
  /** Roughness multiplier over the map (default 1). */
  roughness?: number;
  /** Metalness multiplier over the map (default 1 → the ARM blue channel rules). */
  metalness?: number;
}

const base = import.meta.env.BASE_URL;
const loader = new THREE.TextureLoader();

interface Maps {
  map: THREE.Texture;
  normal: THREE.Texture;
  arm: THREE.Texture;
}
const cache = new Map<string, Maps>();

function mapsFor(spec: SurfaceTextureSpec): Maps {
  const key = `${spec.base}|${spec.repeat[0]}x${spec.repeat[1]}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const url = (name: string): string => `${base}${spec.base}/${name}.jpg`;
  const map = loader.load(url('diffuse'));
  map.colorSpace = THREE.SRGBColorSpace;
  const normal = loader.load(url('normal'));
  const arm = loader.load(url('arm')); // R=AO, G=Roughness, B=Metalness (linear)

  for (const t of [map, normal, arm]) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(spec.repeat[0], spec.repeat[1]);
    t.anisotropy = 8;
    // Shared singletons — the per-city disposer must never free them.
    t.userData.shared = true;
  }

  const maps = { map, normal, arm };
  cache.set(key, maps);
  return maps;
}

/**
 * Build a fresh MeshStandardMaterial from a (cached) surface texture set. Cheap to
 * call per city load; the heavy textures are shared underneath.
 */
export function surfaceMaterial(
  spec: SurfaceTextureSpec,
  side: THREE.Side = THREE.FrontSide,
): THREE.MeshStandardMaterial {
  const m = mapsFor(spec);
  const n = spec.normalScale ?? 1;
  return new THREE.MeshStandardMaterial({
    map: m.map,
    normalMap: m.normal,
    normalScale: new THREE.Vector2(n, n),
    roughnessMap: m.arm,
    metalnessMap: m.arm,
    roughness: spec.roughness ?? 1,
    metalness: spec.metalness ?? 1,
    color: new THREE.Color(spec.tint ?? 0xffffff),
    side,
  });
}
